import React, { useState } from 'react';
import { 
  Typography, 
  TextField, 
  Button, 
  Box,
  Paper,
  CircularProgress,
  Container,
  Card,
  CardContent,
} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import { styled } from '@mui/material/styles';
import { Document, Packer, Paragraph, TextRun } from 'docx';
import { saveAs } from 'file-saver';
import {
  useMountUsageCheck,
  handleGenerateUsage,
  trackGenerationStarted,
  trackGenerationCompleted,
  trackGenerationFailed,
} from '../lib/usageGate';

const TOOL_ID = 'progress_note_template_creator';

const StyledMarkdown = styled(ReactMarkdown)(({ theme }) => ({
  '& h1, & h2, & h3, & h4, & h5, & h6': {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  '& p': {
    marginBottom: theme.spacing(2),
  },
  '& ul, & ol': {
    marginBottom: theme.spacing(2),
    paddingLeft: theme.spacing(3),
  },
  '& li': {
    marginBottom: theme.spacing(1),
  },
  '& code': {
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(0.5, 1),
    borderRadius: theme.shape.borderRadius,
  },
  '& pre': {
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    overflowX: 'auto',
  },
}));

const ProgressNoteTemplateCreator = () => {
  const [description, setDescription] = useState('');
  const [goal, setGoal] = useState('');
  const [template, setTemplate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useMountUsageCheck(TOOL_ID);

  const generateTemplate = async () => {
    setLoading(true);
    setError(null);
    const t0 = Date.now();
    trackGenerationStarted(TOOL_ID, { has_goal: !!goal });
    try {
      const response = await fetch('/api/generateProgressNoteTemplate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ description, goal }),
      });
      if (!response.ok) {
        throw new Error('Failed to generate template');
      }
      const data = await response.json();
      setTemplate(data.template);
      trackGenerationCompleted(TOOL_ID, { duration_ms: Date.now() - t0 });
      handleGenerateUsage(TOOL_ID, data.usage);
    } catch (error) {
      console.error('Error generating template:', error);
      setError('Failed to generate template. Please try again.');
      trackGenerationFailed(TOOL_ID, { error_code: error.message });
    }
    setLoading(false);
  };

  const exportToDocx = () => {
    const doc = new Document({
      sections: [{
        properties: {},
        children: [
          new Paragraph({
            children: [new TextRun(template)],
          }),
        ],
      }],
    });

    Packer.toBlob(doc).then(blob => {
      saveAs(blob, "progress_note_template.docx");
    });
  };

  return (
    <Container maxWidth="md">
      <Paper elevation={0} sx={{ p: 4, backgroundColor: 'white' }}>
        <Box sx={{ mb: 4 }}>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe the format and use case for your progress note (e.g., daily session notes for ABA therapy, focusing on behavior tracking and intervention effectiveness)"
            variant="outlined"
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            multiline
            rows={2}
            value={goal}
            onChange={(e) => setGoal(e.target.value)}
            placeholder="(Optional) Enter a specific goal to tailor the template (e.g., Increase verbal communication skills)"
            variant="outlined"
            sx={{ mb: 2 }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={generateTemplate}
            disabled={loading || !description}
            fullWidth
          >
            Generate Template
          </Button>
        </Box>
        {loading && <CircularProgress />}
        {error && (
          <Typography color="error" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )}
        {template && (
          <Card elevation={2} sx={{ mt: 4 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Generated Progress Note Template:
                </Typography>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={exportToDocx}
                >
                  Export to .docx
                </Button>
              </Box>
              <Box sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
                <StyledMarkdown>{template}</StyledMarkdown>
              </Box>
            </CardContent>
          </Card>
        )}
      </Paper>
    </Container>
  );
};

export default ProgressNoteTemplateCreator;